/**
 * The onboarding copy (AIC-303, AIC-305): one module for the welcome, the orientation tour, the contextual tips and
 * Help → Getting started, so the four never drift apart. Deterministic, reviewed text; no AI assistant.
 *
 * The copy has to match what the code does (plan review, 2026-09-29):
 *   - the system never sends email for the analyst: it creates drafts in the analyst's own Outlook, and the analyst sends them.
 *     The one automatic message is a vendor's one-time code, in live mode (AIC-522, AIC-524);
 *   - Approve waits while a blocking concern is unsettled (the approve gate is on: resolve it, or accept it with a note)
 *     and while a vendor's confidentiality request is still open on Review;
 *   - approving writes the result to Snowflake in the same step, and nothing is ever approved automatically.
 * No example vendor names, figures or records anywhere here. The ids come from @swtracker/core/onboarding, which the
 * server validates; the words can change without resetting anyone's progress.
 */
import type { TipId, TourStepId } from "@swtracker/core/onboarding";

/**
 * The welcome lightbox: the Sep 29 design refinement in AIC-302 ("Implement this welcome design"), which supersedes
 * the earlier Experience wording. `title` is the headline as one line (the dialog's accessible name); the dialog
 * breaks it after the first sentence.
 */
export const WELCOME = {
  kicker: "Your first visit",
  title: "A clear next step. For every survey.",
  titleLines: ["A clear next step.", "For every survey."],
  body: "Welcome to Software Tracker. Prepare vendor surveys, follow responses, and review the evidence, all in one workspace.",
  tour: "Show me around",
  explore: "Explore on my own",
  close: "Close welcome",
  control: "You stay in control. The system never sends email for you: invites and reminders are drafts you send yourself. The only automatic message is a vendor's one-time code, in live mode.",
} as const;

export interface Phase { readonly n: string; readonly title: string; readonly body: string }

/** The welcome's three phases: the whole lifecycle at a glance. */
export const WELCOME_PHASES: readonly Phase[] = [
  { n: "01 / Prepare", title: "Check and invite", body: "Review the interview. Send the draft yourself." },
  { n: "02 / Collect", title: "Follow progress", body: "Your vendor responds by voice or form." },
  { n: "03 / Review", title: "Decide with evidence", body: "Resolve concerns, approve, and check the export." },
];

export interface TourStep {
  readonly id: TourStepId;
  /** The rail link the step points at: the Shell's nav item with this href. */
  readonly nav: string;
  readonly title: string;
  readonly body: string;
}

/** Three stops, each on its page's rail link. The tour explains; it never opens a page or acts on anything. */
export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "today", nav: "/today", title: "Your day starts here.",
    body: "Today brings the work that needs you to the top, and shows what each of the others is waiting on. Start with the next action; you don't need to remember the whole workflow.",
  },
  {
    id: "assignments", nav: "/assignments", title: "One survey. One next step.",
    body: "Assignments lists your vendors for the cycle. Open one to see what's done and whose move it is: prepare from IDC's estimates, review the spec, check contacts and create the invite draft.",
  },
  {
    id: "review", nav: "/review", title: "The evidence comes first.",
    body: "When the vendor responds, Review is where you check the answers against IDC's estimates and settle concerns before you decide. You approve; nothing is approved automatically.",
  },
] as const;

export const TOUR_FALLBACK = "This page's link isn't on screen right now. You'll find it in the menu.";
/** The toast when the tour is finished: learning progress, never survey progress. */
export const TOUR_DONE = "Tour complete. Your survey progress hasn't changed.";

export type Actor = "you" | "system" | "vendor";
export const ACTOR_LABEL: Readonly<Record<Actor, string>> = { you: "You", system: "The system", vendor: "The vendor" };

export interface LifecycleStep { readonly actor: Actor; readonly title: string; readonly body: string }

/** The survey lifecycle in four stages, as the code runs it. `buildMode` adds what BUILD mode changes. */
export function lifecycle(buildMode: boolean): readonly LifecycleStep[] {
  return [
    {
      actor: "you", title: "You prepare",
      body: `Assignments arrive from the IDC Data Layer. Start prep and the system writes the interview spec from IDC's estimates; review it, check the contacts, and connect Outlook when the portal asks. The invite draft goes to your Outlook Drafts: you check it and send it from Outlook. The system never sends email for you; the only automatic message is a vendor's one-time code, in live mode.${buildMode ? " In build mode drafts go to the build tester only, and nothing reaches a vendor." : ""}`,
    },
    {
      actor: "vendor", title: "The vendor responds",
      body: "The vendor answers in an AI voice interview or on a text form, whichever they choose, then confirms their answers. Ending a call and confirming the answers are separate steps.",
    },
    {
      actor: "you", title: "You review",
      body: "Automated checks compare the answers with IDC's estimates and raise concerns. Check the evidence, then settle them: Approve waits while a blocking concern is unsettled (resolve it, or accept it with a note) and while a vendor's request to keep something confidential is open on Review.",
    },
    {
      actor: "system", title: "The system exports",
      body: "Approving writes the result to the Data Layer in the same step, so check the outcome: if that write fails, the result stays waiting for you. Nothing is approved automatically.",
    },
  ];
}

/** Getting started's note for someone with nothing assigned yet. */
export const NO_ASSIGNMENTS_NOTE = "No assignments yet? They appear under Assignments once the Data Layer assigns vendors to you and an admin confirms your analyst mapping in Admin → People. You can still take the tour and read this page.";
/** Getting started's way back to the survey progress: an assignment's page (from return_to), else the list. */
export const PROGRESS_LINK = { assignment: "Back to this assignment's progress", list: "See your assignments' progress" } as const;

export interface Role { readonly who: string; readonly does: readonly string[] }

/** "Who does what": analyst, system and vendor responsibilities, then what managers and admins add. */
export const WHO_DOES_WHAT: readonly Role[] = [
  { who: "You, the analyst", does: ["Start prep and approve or reject the spec", "Send the invite draft from your own Outlook", "Settle concerns and approve, reject or ask for a follow-up"] },
  { who: "The system", does: ["Syncs assignments from the Data Layer", "Prepares the spec and creates drafts for you to send", "Runs the interview, verifies the answers and writes approved results to the Data Layer"] },
  { who: "The vendor", does: ["Answers by voice or on the form", "Confirms their answers"] },
  { who: "Managers and admins", does: ["Managers see their team's work and can reassign it", "Admins also manage analysts, cycles and settings"] },
];

export interface Tip { readonly title: string; readonly body: string }

/** Inline, dismissible guidance the first time a user reaches the task. `buildMode` adds what BUILD mode changes. */
export function tip(id: TipId, buildMode: boolean, practice = false): Tip {
  switch (id) {
    case "spec_approval":
      if (practice) return { title: "Reviewing the spec", body: "Check the questions and IDC's figures before you approve. Approving the spec creates the practice invite draft in the practice outbox; nothing is emailed." };
      return { title: "Reviewing the spec", body: "Check the questions and IDC's figures before you approve. Approving the spec can create the invite draft in your Outlook Drafts; it sends nothing." };
    case "invite_draft":
      if (practice) return { title: "The practice invite stays in the outbox", body: "The practice draft lands in the practice outbox. Open it there to see how it reads: nothing is emailed, and nothing reaches Outlook." };
      return {
        title: "The invite is yours to send",
        body: `The draft lands in your Outlook Drafts. Open it in Outlook, check it and send it yourself: the system never sends email for you.${buildMode ? " In build mode it is addressed to the build tester only." : ""}`,
      };
    case "result_review":
      return {
        title: "Before you approve",
        body: "Approval waits for any blocking concern and any separate confidentiality hold.",
      };
  }
}

/** AIC-463: where Getting started and the end of the tour point to a rehearsal on a copy (nothing reaches vendors, Outlook or Snowflake). */
export const PRACTICE_LINK = { label: "Try a practice run", to: "/practice" } as const;
